import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AskRyanPanel from "@/components/AskRyanPanel";
import { KnowledgeGraph } from "@/components/KnowledgeGraph";

// Dashboard sub-components
import { Sidebar } from "@/components/dashboard/Sidebar";
import { BookmarkCard } from "@/components/dashboard/BookmarkCard";
import { CollectionModal } from "@/components/dashboard/CollectionModal";
import { BookmarkEditModal } from "@/components/dashboard/BookmarkEditModal";
import { SmartGroupsModal } from "@/components/dashboard/SmartGroupsModal";
import { UpgradeModal } from "@/components/dashboard/UpgradeModal";
import { SettingsModal } from "@/components/dashboard/SettingsModal";
import { ShareModal } from "@/components/dashboard/ShareModal";
import { RelatedModal } from "@/components/dashboard/RelatedModal";

const Dashboard = () => {
  const { user, logout, login } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const passedUser = location.state?.user || user;

  // State
  const [bookmarks, setBookmarks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({ total_bookmarks: 0, total_collections: 0 });
  const [subscription, setSubscription] = useState({ subscription_status: "free", loading: true });
  const [usage, setUsage] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Modal states
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [askRyanOpen, setAskRyanOpen] = useState(false);

  // Notion state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [notionStatus, setNotionStatus] = useState({ connected: false });
  const [notionToken, setNotionToken] = useState("");
  const [notionSyncing, setNotionSyncing] = useState(false);

  // Bulk selection state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedBookmarks, setSelectedBookmarks] = useState(new Set());

  // Share state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [sharingCollectionId, setSharingCollectionId] = useState(null);
  const [shareLevel, setShareLevel] = useState("shared_link");
  const [collabEmail, setCollabEmail] = useState("");
  const [collaborators, setCollaborators] = useState([]);

  // Related bookmarks
  const [relatedBookmarks, setRelatedBookmarks] = useState([]);
  const [viewingBookmark, setViewingBookmark] = useState(null);

  // Image upload
  const [uploadingImage, setUploadingImage] = useState(false);

  // Upgrade nudge
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // Smart Collections
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [smartGroupsOpen, setSmartGroupsOpen] = useState(false);

  // Knowledge Graph
  const [graphOpen, setGraphOpen] = useState(false);

  // Sort
  const [sortBy, setSortBy] = useState("newest");

  // Tags
  const [allTags, setAllTags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);

  // Smart Filters
  const [filterOptions, setFilterOptions] = useState({ sources: [], roles: [] });
  const [activeRole, setActiveRole] = useState(null);
  const [activeSource, setActiveSource] = useState(null);
  const [activeDateRange, setActiveDateRange] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Onboarding
  const [onboarding, setOnboarding] = useState(null);

  // Cap-hit modal
  const [capHitModalOpen, setCapHitModalOpen] = useState(false);

  // Form states
  const [collectionForm, setCollectionForm] = useState({ name: "", description: "", color: "#E8A030" });
  const [bookmarkForm, setBookmarkForm] = useState({ collection_id: "", annotation: "", tags: [] });

  // Check for payment return
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
      setSearchParams({});
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment cancelled");
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.error("Payment verification timed out. Please refresh."); return; }
    try {
      setPaymentProcessing(true);
      const response = await axios.get(`${API}/subscription/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Welcome to Ryan Signal!");
        fetchSubscription();
        setPaymentProcessing(false);
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired.");
        setPaymentProcessing(false);
      } else { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
    } catch (error) { console.error("Payment status error:", error); setPaymentProcessing(false); }
  };

  const fetchSubscription = async () => {
    try { const response = await axios.get(`${API}/subscription/my-subscription`); setSubscription(response.data); }
    catch (error) { console.error("Subscription fetch error:", error); }
  };

  const fetchUsage = async () => {
    try { const response = await axios.get(`${API}/usage`); setUsage(response.data); }
    catch (error) { console.error("Usage fetch error:", error); }
  };

  const fetchSmartGroups = async () => {
    try { const response = await axios.get(`${API}/bookmarks/smart-groups`); setSmartSuggestions(response.data.suggestions || []); }
    catch (error) { console.error("Smart groups error:", error); }
  };

  const fetchTags = async () => {
    try { const response = await axios.get(`${API}/tags`); setAllTags(response.data.tags || []); }
    catch (error) { console.error("Tags fetch error:", error); }
  };

  const fetchFilterOptions = async () => {
    try { const response = await axios.get(`${API}/bookmarks/filters`); setFilterOptions(response.data); }
    catch (error) { console.error("Filter options fetch error:", error); }
  };

  // Event tracking helper
  const trackEvent = async (event, metadata = {}) => {
    try { await axios.post(`${API}/events/track`, { event, metadata }); }
    catch (e) { /* silent */ }
  };

  // Onboarding fetch
  const fetchOnboarding = async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`);
      setOnboarding(res.data);
    } catch (e) { /* silent */ }
  };

  const completeOnboardingStep = async (step) => {
    try {
      await axios.put(`${API}/onboarding/complete-step`, { step });
      trackEvent("onboarding_step_completed", { step });
      fetchOnboarding();
    } catch (e) { /* silent */ }
  };

  const dismissOnboarding = async () => {
    try {
      await axios.put(`${API}/onboarding/dismiss`);
      setOnboarding(prev => ({ ...prev, dismissed: true }));
    } catch (e) { /* silent */ }
  };

  const applySmartGroup = async (suggestion) => {
    try {
      await axios.post(`${API}/bookmarks/smart-groups/apply`, { name: suggestion.suggested_name, bookmark_ids: suggestion.bookmark_ids });
      toast.success(`Volume "${suggestion.suggested_name}" created with ${suggestion.count} entries`);
      fetchData(); fetchSmartGroups();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create group"); }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const sortParams = {};
      if (sortBy === "oldest") { sortParams.sort_by = "oldest"; }
      else if (sortBy === "alpha") { sortParams.sort_by = "alpha"; sortParams.sort_order = "asc"; }

      // Smart filters
      if (activeRole) sortParams.role = activeRole;
      if (activeSource) sortParams.source = activeSource;
      if (activeDateRange) {
        const now = new Date();
        let from;
        if (activeDateRange === "today") {
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (activeDateRange === "week") {
          from = new Date(now); from.setDate(from.getDate() - 7);
        } else if (activeDateRange === "month") {
          from = new Date(now); from.setDate(from.getDate() - 30);
        }
        if (from) sortParams.date_from = from.toISOString();
      }

      const [bookmarksRes, collectionsRes, statsRes, subRes] = await Promise.all([
        axios.get(`${API}/bookmarks`, { params: { collection_id: selectedCollection || undefined, tag: activeTag || undefined, ...sortParams } }),
        axios.get(`${API}/collections`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/subscription/my-subscription`),
      ]);
      setBookmarks(bookmarksRes.data);
      setCollections(collectionsRes.data);
      setStats(statsRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        try { await login(); } catch { console.error("Re-auth failed"); }
      } else { console.error("Data fetch error:", error); toast.error("Failed to load data"); }
    } finally { setLoading(false); setSubscription(prev => ({ ...prev, loading: false })); }
  }, [selectedCollection, activeTag, sortBy, activeRole, activeSource, activeDateRange]);

  useEffect(() => { fetchData(); fetchUsage(); fetchSmartGroups(); fetchTags(); fetchFilterOptions(); fetchOnboarding(); }, [fetchData]);

  // Session keep-alive
  useEffect(() => {
    const interval = setInterval(async () => {
      try { await axios.get(`${API}/auth/me`); } catch (e) { console.warn("Session keep-alive failed:", e.message); }
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Search debounce
  useEffect(() => {
    if (!searchQuery) { fetchData(); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/bookmarks`, { params: { search: searchQuery, collection_id: selectedCollection || undefined } });
        setBookmarks(res.data);
      } catch (e) { console.error("Search error:", e); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCollection]);

  // Collection CRUD
  const handleCreateCollection = async () => {
    try {
      await axios.post(`${API}/collections`, collectionForm);
      toast.success("Volume created"); setCollectionModalOpen(false); fetchData(); fetchUsage();
    } catch (error) {
      if (error.response?.status === 403) { toast.error(error.response.data.detail); setCollectionModalOpen(false); setUpgradeModalOpen(true); }
      else { toast.error("Failed to create volume"); }
    }
  };

  const handleUpdateCollection = async () => {
    try {
      await axios.put(`${API}/collections/${editingCollection.collection_id}`, collectionForm);
      toast.success("Volume updated"); setCollectionModalOpen(false); fetchData();
    } catch (error) { toast.error("Failed to update volume"); }
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm("Delete this volume? Bookmarks will be moved to Uncategorized.")) return;
    try {
      await axios.delete(`${API}/collections/${collectionId}`);
      toast.success("Volume deleted");
      if (selectedCollection === collectionId) setSelectedCollection(null);
      fetchData(); fetchUsage();
    } catch (error) { toast.error("Failed to delete volume"); }
  };

  const handleTogglePin = async (bookmarkId) => {
    try {
      const res = await axios.put(`${API}/bookmarks/${bookmarkId}/pin`);
      toast.success(res.data.pinned ? "Pinned to top" : "Unpinned");
      fetchData();
    } catch (error) { toast.error("Failed to pin bookmark"); }
  };


  const handleUpdateBookmark = async () => {
    try {
      await axios.put(`${API}/bookmarks/${editingBookmark.bookmark_id}`, bookmarkForm);
      toast.success("Bookmark updated"); setBookmarkModalOpen(false); fetchData(); fetchTags();
    } catch (error) { toast.error("Failed to update bookmark"); }
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    if (!window.confirm("Delete this bookmark?")) return;
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}`);
      toast.success("Bookmark deleted"); fetchData(); fetchUsage();
    } catch (error) { toast.error("Failed to delete bookmark"); }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/export/${format}`, { params: { collection_id: selectedCollection || undefined } });
      const data = format === "json" ? JSON.stringify(response.data, null, 2) : response.data.markdown;
      const blob = new Blob([data], { type: format === "json" ? "application/json" : "text/markdown" });
      downloadBlob(blob, `ryan-bookmarks.${format === "json" ? "json" : "md"}`);
      toast.success(`Exported as ${format.toUpperCase()}`);
      setExportModalOpen(false);
    } catch (error) { toast.error("Export failed"); }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a);
    a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const [copiedId, setCopiedId] = useState(null);
  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getCollectionName = (collectionId) => collections.find(c => c.collection_id === collectionId)?.name || "Uncategorized";
  const getCollectionColor = (collectionId) => collections.find(c => c.collection_id === collectionId)?.color || "#4A3A1A";

  const handleUpgrade = async (planId) => {
    try {
      const response = await axios.post(`${API}/subscription/checkout`, { plan_id: planId, origin_url: window.location.origin });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) { console.error("Checkout error:", error); toast.error("Failed to start checkout."); }
  };

  // === Notion Functions ===
  const fetchNotionStatus = async () => {
    try { const res = await axios.get(`${API}/notion/status`); setNotionStatus(res.data); }
    catch (e) { console.error("Notion status error:", e); }
  };
  useEffect(() => { fetchNotionStatus(); }, []);

  const handleConnectNotion = async () => {
    if (!notionToken.trim()) return;
    try {
      const res = await axios.post(`${API}/notion/connect`, { token: notionToken.trim() });
      if (res.data.connected) { toast.success("Notion connected!"); setNotionStatus(res.data); setNotionToken(""); fetchNotionStatus(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to connect Notion"); }
  };

  const handleNotionSync = async () => {
    setNotionSyncing(true);
    try { const res = await axios.post(`${API}/notion/sync`); toast.success(`Synced ${res.data.synced} bookmarks to Notion (${res.data.skipped} already synced)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setNotionSyncing(false); }
  };

  const handleDisconnectNotion = async () => {
    if (!window.confirm("Disconnect Notion? Existing synced data will remain in Notion.")) return;
    try { await axios.delete(`${API}/notion/disconnect`); setNotionStatus({ connected: false }); toast.success("Notion disconnected"); }
    catch (e) { toast.error("Failed to disconnect"); }
  };

  // === Share Functions ===
  const handleShareCollection = async (colId, level) => {
    try {
      const res = await axios.post(`${API}/collections/${colId}/share`, { share_level: level || shareLevel });
      setSharingCollectionId(colId); setShareInfo(res.data);
      setShareLevel(res.data.share_level || "shared_link"); setCollaborators(res.data.collaborators || []);
      setShareModalOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create share link"); }
  };

  const addCollaborator = async () => {
    if (!collabEmail.trim()) return;
    try {
      const res = await axios.post(`${API}/collections/${sharingCollectionId}/collaborators`, { action: "add", email: collabEmail.trim() });
      setCollaborators(res.data.collaborators); setCollabEmail(""); toast.success(`${collabEmail} added as collaborator`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add collaborator"); }
  };

  const removeCollaborator = async (email) => {
    try {
      const res = await axios.post(`${API}/collections/${sharingCollectionId}/collaborators`, { action: "remove", email });
      setCollaborators(res.data.collaborators); toast.success("Collaborator removed");
    } catch (e) { toast.error("Failed to remove collaborator"); }
  };

  const handleUnshareCollection = async (colId) => {
    try { await axios.delete(`${API}/collections/${colId}/share`); setShareModalOpen(false); setShareInfo(null); fetchData(); toast.success("Sharing revoked — volume is now private"); }
    catch (e) { toast.error("Failed to revoke share"); }
  };

  const handleShareLibrary = async () => {
    try { const res = await axios.post(`${API}/library/share`); navigator.clipboard.writeText(`${window.location.origin}/shared/library/${res.data.library_token}`); toast.success("Library link copied!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Library sharing requires Synthesis tier"); }
  };

  const copyShareLink = () => {
    if (shareInfo?.share_url) { navigator.clipboard.writeText(`${window.location.origin}${shareInfo.share_url}`); toast.success("Link copied!"); }
  };

  // === Bulk Functions ===
  const toggleBulkSelect = (bookmarkId) => {
    setSelectedBookmarks(prev => { const next = new Set(prev); if (next.has(bookmarkId)) next.delete(bookmarkId); else next.add(bookmarkId); return next; });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedBookmarks.size} bookmarks?`)) return;
    try { await axios.post(`${API}/bookmarks/bulk-delete`, { bookmark_ids: [...selectedBookmarks] }); toast.success(`Deleted ${selectedBookmarks.size} bookmarks`); setSelectedBookmarks(new Set()); setBulkMode(false); fetchData(); fetchUsage(); }
    catch (e) { toast.error("Bulk delete failed"); }
  };

  const handleBulkMove = async (targetCollectionId) => {
    try { await axios.post(`${API}/bookmarks/bulk-move`, { bookmark_ids: [...selectedBookmarks], collection_id: targetCollectionId || null }); toast.success(`Moved ${selectedBookmarks.size} bookmarks`); setSelectedBookmarks(new Set()); setBulkMode(false); fetchData(); }
    catch (e) { toast.error("Bulk move failed"); }
  };

  // === Related & Images ===
  const fetchRelated = async (bookmarkId) => {
    try { const res = await axios.get(`${API}/bookmarks/${bookmarkId}/related`); setRelatedBookmarks(res.data); }
    catch (e) { setRelatedBookmarks([]); }
  };

  const recordView = async (bookmarkId) => {
    try { await axios.post(`${API}/bookmarks/${bookmarkId}/view`); }
    catch (e) { /* silent */ }
  };

  const handleImageUpload = async (bookmarkId, file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData(); formData.append("file", file);
      const res = await axios.post(`${API}/bookmarks/${bookmarkId}/images`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.ocr_text ? "Image uploaded & text extracted!" : "Image uploaded!"); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Image upload failed"); }
    finally { setUploadingImage(false); }
  };

  const handleDeleteImage = async (bookmarkId, imageId) => {
    try { await axios.delete(`${API}/bookmarks/${bookmarkId}/images/${imageId}`); toast.success("Image removed"); fetchData(); }
    catch (e) { toast.error("Failed to remove image"); }
  };

  const getImageUrl = (storagePath) => {
    const session = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith("session_token="));
    const token = session ? session.split("=")[1] : "";
    return `${API}/files/${storagePath}?auth=${token}`;
  };

  const isPro = subscription.subscription_status === "active";
  const currentPlan = subscription.subscription_plan;

  return (
    <div className="h-screen w-screen overflow-hidden flex bg-[#0A0600]" data-testid="dashboard">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}

      <Sidebar
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        passedUser={passedUser} isPro={isPro} currentPlan={currentPlan} askRyanOpen={askRyanOpen} coveredByTeam={subscription.covered_by_team} subscriptionLoading={!!subscription.loading}
        stats={stats} usage={usage} collections={collections}
        selectedCollection={selectedCollection} setSelectedCollection={setSelectedCollection}
        smartSuggestions={smartSuggestions}
        setUpgradeModalOpen={setUpgradeModalOpen}
        setCollectionModalOpen={setCollectionModalOpen}
        setEditingCollection={setEditingCollection} setCollectionForm={setCollectionForm}
        setAskRyanOpen={setAskRyanOpen} setSmartGroupsOpen={setSmartGroupsOpen}
        setExportModalOpen={setExportModalOpen} setSettingsModalOpen={setSettingsModalOpen}
        handleShareCollection={handleShareCollection}
        handleDeleteCollection={handleDeleteCollection}
        logout={logout}
      />

      {/* Main Content */}
      <main className="main-content-dark flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Header */}
        <header className="border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004]">
          <div className="h-[60px] flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden text-[#8C7A5A] hover:text-[#E8A030]">☰</button>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A3A1A]">⌕</span>
                <input type="text" placeholder="Search bookmarks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="input-dark w-64 md:w-80 pl-9" data-testid="search-input" />
              </div>
              <button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 border transition-all text-[9px] font-mono uppercase tracking-[1px] ${
                  (activeRole || activeSource || activeDateRange)
                    ? 'border-[rgba(232,160,48,0.3)] bg-[rgba(232,160,48,0.08)] text-[#E8A030]'
                    : 'border-[rgba(232,160,48,0.1)] text-[#8C7A5A] hover:text-[#E8A030] hover:border-[rgba(232,160,48,0.2)]'
                }`}
                data-testid="toggle-filters-btn"
              >
                <span className="text-[10px]">&#9660;</span> Filters
                {(activeRole || activeSource || activeDateRange) && (
                  <span className="w-1.5 h-1.5 bg-[#E8A030] rounded-full" />
                )}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/analytics" className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-[rgba(232,160,48,0.15)] hover:border-[rgba(232,160,48,0.3)] bg-[rgba(232,160,48,0.02)] hover:bg-[rgba(232,160,48,0.06)] transition-all" data-testid="analytics-btn">
                <span className="text-[#E8A030] text-xs">▤</span>
                <span className="font-mono text-[9px] text-[#8C7A5A] uppercase tracking-[1px]">Analytics</span>
              </Link>
            <button onClick={() => setGraphOpen(true)} className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-[rgba(232,160,48,0.15)] hover:border-[rgba(232,160,48,0.3)] bg-[rgba(232,160,48,0.02)] hover:bg-[rgba(232,160,48,0.06)] transition-all" data-testid="knowledge-graph-btn">
              <span className="text-[#7AB8A0] text-xs">⬡</span>
              <span className="font-mono text-[9px] text-[#8C7A5A] uppercase tracking-[1px]">Graph</span>
            </button>
            {currentPlan && (currentPlan.includes('synthesis') || currentPlan.includes('enterprise')) && (
              <button onClick={handleShareLibrary} className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-[rgba(160,140,200,0.2)] hover:border-[rgba(160,140,200,0.4)] bg-[rgba(160,140,200,0.04)] hover:bg-[rgba(160,140,200,0.08)] transition-all" data-testid="share-library-btn">
                <span className="text-[#A08CC8] text-xs">⬡</span>
                <span className="font-mono text-[9px] text-[#A08CC8] uppercase tracking-[1px]">Share Library</span>
              </button>
            )}
            <button onClick={() => setAskRyanOpen(true)} className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-[rgba(232,160,48,0.2)] hover:border-[rgba(232,160,48,0.4)] bg-[rgba(232,160,48,0.04)] hover:bg-[rgba(232,160,48,0.08)] transition-all" data-testid="ask-ryan-header-btn">
              <span className="text-[#E8A030] font-serif font-bold text-xs">R</span>
              <span className="font-mono text-[9px] text-[#E8A030] uppercase tracking-[1px]">Ask Ryan</span>
            </button>
          </div>
          </div>

          {/* Smart Filter Chips */}
          {filtersExpanded && (
            <div className="px-6 py-2.5 border-t border-[rgba(232,160,48,0.06)] flex items-center gap-2 flex-wrap" data-testid="filter-chips-bar">
              <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mr-1">Filter:</span>

              {/* Role chips */}
              {filterOptions.roles.map(r => (
                <button
                  key={r.name}
                  onClick={() => setActiveRole(activeRole === r.name ? null : r.name)}
                  className={`px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.5px] border transition-all ${
                    activeRole === r.name
                      ? 'border-[#7AB8A0] bg-[rgba(122,184,160,0.12)] text-[#7AB8A0]'
                      : 'border-[rgba(232,160,48,0.1)] text-[#8C7A5A] hover:border-[rgba(232,160,48,0.2)] hover:text-[#E8A030]'
                  }`}
                  data-testid={`filter-role-${r.name}`}
                >
                  {r.name} <span className="text-[7px] opacity-60 ml-1">{r.count}</span>
                </button>
              ))}

              {/* Source chips */}
              {filterOptions.sources.map(s => (
                <button
                  key={s.name}
                  onClick={() => setActiveSource(activeSource === s.name ? null : s.name)}
                  className={`px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.5px] border transition-all ${
                    activeSource === s.name
                      ? 'border-[#A08CC8] bg-[rgba(160,140,200,0.12)] text-[#A08CC8]'
                      : 'border-[rgba(232,160,48,0.1)] text-[#8C7A5A] hover:border-[rgba(232,160,48,0.2)] hover:text-[#E8A030]'
                  }`}
                  data-testid={`filter-source-${s.name}`}
                >
                  {s.name} <span className="text-[7px] opacity-60 ml-1">{s.count}</span>
                </button>
              ))}

              {/* Date range chips */}
              {[
                { key: "today", label: "Today" },
                { key: "week", label: "This Week" },
                { key: "month", label: "Last 30 Days" },
              ].map(d => (
                <button
                  key={d.key}
                  onClick={() => setActiveDateRange(activeDateRange === d.key ? null : d.key)}
                  className={`px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.5px] border transition-all ${
                    activeDateRange === d.key
                      ? 'border-[#E8A030] bg-[rgba(232,160,48,0.12)] text-[#E8A030]'
                      : 'border-[rgba(232,160,48,0.1)] text-[#8C7A5A] hover:border-[rgba(232,160,48,0.2)] hover:text-[#E8A030]'
                  }`}
                  data-testid={`filter-date-${d.key}`}
                >
                  {d.label}
                </button>
              ))}

              {/* Clear all filters */}
              {(activeRole || activeSource || activeDateRange) && (
                <button
                  onClick={() => { setActiveRole(null); setActiveSource(null); setActiveDateRange(null); }}
                  className="px-2 py-1 font-mono text-[9px] text-red-400 hover:text-red-300 uppercase tracking-[0.5px] transition-colors"
                  data-testid="clear-all-filters"
                >
                  Clear All
                </button>
              )}
            </div>
          )}
        </header>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-[#FDF8EE] tracking-[-0.5px]">
                {selectedCollection ? collections.find(c => c.collection_id === selectedCollection)?.name || "Volume" : "All Bookmarks"}
              </h1>
              <p className="font-mono text-[10px] text-[#4A3A1A] mt-1 uppercase tracking-[1px]">
                {bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''}{searchQuery && ` matching "${searchQuery}"`}{activeTag && ` tagged #${activeTag}`}{activeRole && ` · ${activeRole}`}{activeSource && ` · ${activeSource}`}{activeDateRange && ` · ${activeDateRange === 'today' ? 'today' : activeDateRange === 'week' ? 'this week' : 'last 30 days'}`}
              </p>
              {activeTag && (
                <button onClick={() => setActiveTag(null)} className="mt-1.5 flex items-center gap-1.5 px-2 py-0.5 bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.15)] hover:border-[rgba(232,160,48,0.3)] transition-colors" data-testid="clear-tag-filter">
                  <span className="text-[9px] text-[#E8A030] font-mono">#{activeTag}</span>
                  <span className="text-[#8C7A5A] text-xs">&times;</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {bulkMode ? (
                <>
                  <span className="font-mono text-[9px] text-[#8C7A5A]">{selectedBookmarks.size} selected</span>
                  {selectedBookmarks.size > 0 && (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="font-mono text-[9px] text-[#E8A030] hover:bg-[rgba(232,160,48,0.1)] px-2 py-1 border border-[rgba(232,160,48,0.2)] uppercase tracking-[1px]" data-testid="bulk-move-trigger">Move</button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1C1004] border-[rgba(232,160,48,0.15)] text-[#F5ECD0]">
                          <DropdownMenuItem onClick={() => handleBulkMove(null)} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]">Uncategorized</DropdownMenuItem>
                          {collections.map(c => (
                            <DropdownMenuItem key={c.collection_id} onClick={() => handleBulkMove(c.collection_id)} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]">
                              <span style={{color: c.color}} className="mr-2">◈</span>{c.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <button onClick={handleBulkDelete} className="font-mono text-[9px] text-red-400 hover:bg-[rgba(255,0,0,0.1)] px-2 py-1 border border-[rgba(255,0,0,0.2)] uppercase tracking-[1px]" data-testid="bulk-delete-btn">Delete</button>
                    </>
                  )}
                  <button onClick={() => { setBulkMode(false); setSelectedBookmarks(new Set()); }} className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] px-2 py-1 uppercase tracking-[1px]" data-testid="bulk-cancel">Cancel</button>
                </>
              ) : (
                <>
                  {/* Sort Controls */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] px-2 py-1 uppercase tracking-[1px] transition-colors border border-[rgba(232,160,48,0.1)]" data-testid="sort-trigger">
                        Sort: {sortBy === "newest" ? "Newest" : sortBy === "oldest" ? "Oldest" : "A-Z"}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#1C1004] border-[rgba(232,160,48,0.15)] text-[#F5ECD0]">
                      <DropdownMenuItem onClick={() => setSortBy("newest")} className={`text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)] ${sortBy === 'newest' ? 'text-[#E8A030]' : ''}`}>Newest First</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy("oldest")} className={`text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)] ${sortBy === 'oldest' ? 'text-[#E8A030]' : ''}`}>Oldest First</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy("alpha")} className={`text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)] ${sortBy === 'alpha' ? 'text-[#E8A030]' : ''}`}>Alphabetical</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {bookmarks.length > 0 && (
                    <button onClick={() => setBulkMode(true)} className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] px-2 py-1 uppercase tracking-[1px] transition-colors" data-testid="bulk-select-btn">Select</button>
                  )}
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="spinner-ember"></div></div>
          ) : (
            <>
              {/* Onboarding Checklist */}
              {onboarding && !onboarding.dismissed && (
                <div className="mb-5 p-5 bg-[rgba(232,160,48,0.04)] border border-[rgba(232,160,48,0.15)]" data-testid="onboarding-checklist">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-serif text-sm font-bold text-[#FDF8EE]">Welcome to Ryan</h3>
                      <p className="font-mono text-[9px] text-[#D9C8A6] mt-0.5 uppercase tracking-[0.5px]">Everything you save, in one place you can actually find</p>
                    </div>
                    <button onClick={dismissOnboarding} className="text-[#A8987A] hover:text-[#E8A030] transition-colors text-lg leading-none" data-testid="onboarding-dismiss">&times;</button>
                  </div>
                  <p className="text-[12px] text-[#E6D9BF] mb-4">Three quick steps to get started:</p>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-3" data-testid="onboarding-step-pin">
                      <div className={`w-5 h-5 border flex items-center justify-center shrink-0 ${onboarding.pin ? 'border-[#7AB8A0] bg-[rgba(122,184,160,0.1)]' : 'border-[rgba(232,160,48,0.2)]'}`}>
                        {onboarding.pin && <span className="text-[#7AB8A0] text-xs">&#10003;</span>}
                      </div>
                      <div className="flex-1">
                        <span className={`text-[12px] ${onboarding.pin ? 'text-[#7AB8A0] line-through' : 'text-[#FDF8EE]'}`}>Pin Ryan to your toolbar</span>
                        <span className="text-[11px] text-[#D9C8A6] ml-2">Click the puzzle icon, then the pin</span>
                      </div>
                      {!onboarding.pin && (
                        <button onClick={() => completeOnboardingStep("pin")} className="font-mono text-[9px] text-[#E8A030] border border-[rgba(232,160,48,0.2)] px-2 py-1 hover:bg-[rgba(232,160,48,0.08)] transition-colors uppercase tracking-[0.5px]" data-testid="onboarding-pin-done">Done</button>
                      )}
                    </div>
                    <div className="flex items-center gap-3" data-testid="onboarding-step-save">
                      <div className={`w-5 h-5 border flex items-center justify-center shrink-0 ${onboarding.first_save ? 'border-[#7AB8A0] bg-[rgba(122,184,160,0.1)]' : 'border-[rgba(232,160,48,0.2)]'}`}>
                        {onboarding.first_save && <span className="text-[#7AB8A0] text-xs">&#10003;</span>}
                      </div>
                      <span className={`text-[12px] ${onboarding.first_save ? 'text-[#7AB8A0] line-through' : 'text-[#FDF8EE]'}`}>Save your first item</span>
                    </div>
                    <div className="flex items-center gap-3" data-testid="onboarding-step-volume">
                      <div className={`w-5 h-5 border flex items-center justify-center shrink-0 ${onboarding.add_volume ? 'border-[#7AB8A0] bg-[rgba(122,184,160,0.1)]' : 'border-[rgba(232,160,48,0.2)]'}`}>
                        {onboarding.add_volume && <span className="text-[#7AB8A0] text-xs">&#10003;</span>}
                      </div>
                      <span className={`text-[12px] ${onboarding.add_volume ? 'text-[#7AB8A0] line-through' : 'text-[#FDF8EE]'}`}>Add it to a volume</span>
                    </div>
                  </div>
                  {!isPro && <p className="font-mono text-[10px] text-[#F0B85A] mt-4 uppercase tracking-[0.5px]" data-testid="onboarding-free-limit">You can save 10 items on the free plan.</p>}
                </div>
              )}

              {/* Upgrade Nudge Banner — at 8 saves */}
              {!nudgeDismissed && !isPro && usage && usage.plan === "free" && (
                (usage.bookmarks?.used >= 8) && (
                  <div className="mb-5 p-4 bg-[rgba(232,160,48,0.06)] border border-[rgba(232,160,48,0.18)] flex items-center justify-between gap-4 animate-fade-up" data-testid="upgrade-nudge-banner">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-[#E8A030] text-lg shrink-0">◈</span>
                      <div className="min-w-0">
                        <p className="text-sm text-[#FDF8EE] font-medium">
                          {usage.bookmarks?.remaining === 0
                            ? "You've reached your free limit"
                            : `You've used ${usage.bookmarks?.used} of ${usage.bookmarks?.limit} free saves. See what Signal includes.`}
                        </p>
                        <p className="text-[11px] text-[#8C7A5A] mt-0.5">
                          Upgrade for unlimited saves, Ask Ryan AI, Notion sync, and more
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => { trackEvent("upgrade_click", { source: "warning_banner" }); setUpgradeModalOpen(true); }} className="btn-primary text-[10px] px-4 py-1.5" data-testid="nudge-upgrade-btn">See Signal</button>
                      <button onClick={() => setNudgeDismissed(true)} className="text-[#4A3A1A] hover:text-[#8C7A5A] transition-colors text-lg leading-none" data-testid="nudge-dismiss-btn">&times;</button>
                    </div>
                  </div>
                )
              )}

              {bookmarks.length === 0 ? (
                <div className="empty-state-dark">
                  <div className="text-4xl mb-4 text-[#4A3A1A]">◈</div>
                  <h3 className="font-serif text-lg font-bold text-[#FDF8EE] mb-2">Save your first item to get started</h3>
                  <p className="text-sm text-[#8C7A5A] max-w-sm mx-auto mb-6">Use the Chrome extension to bookmark specific moments from your AI conversations. Your library starts here.</p>
                  <a href="https://chromewebstore.google.com" target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="empty-install-ext-btn">Install Extension</a>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {bookmarks.map((bookmark, index) => (
                    <BookmarkCard
                      key={bookmark.bookmark_id} bookmark={bookmark} index={index}
                      bulkMode={bulkMode} selectedBookmarks={selectedBookmarks} toggleBulkSelect={toggleBulkSelect}
                      getCollectionColor={getCollectionColor} getCollectionName={getCollectionName} getImageUrl={getImageUrl}
                      copyToClipboard={copyToClipboard} copiedId={copiedId}
                      setEditingBookmark={setEditingBookmark} setBookmarkForm={setBookmarkForm} setBookmarkModalOpen={setBookmarkModalOpen}
                      setViewingBookmark={setViewingBookmark} fetchRelated={fetchRelated} recordView={recordView}
                      handleDeleteBookmark={handleDeleteBookmark} handleTogglePin={handleTogglePin}
                      onTagClick={(tag) => setActiveTag(tag)} searchQuery={searchQuery}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      <CollectionModal
        open={collectionModalOpen} onOpenChange={setCollectionModalOpen}
        editing={editingCollection} form={collectionForm} setForm={setCollectionForm}
        onSave={editingCollection ? handleUpdateCollection : handleCreateCollection}
      />

      <BookmarkEditModal
        open={bookmarkModalOpen} onOpenChange={setBookmarkModalOpen}
        form={bookmarkForm} setForm={setBookmarkForm} collections={collections}
        editingBookmark={editingBookmark} getImageUrl={getImageUrl} uploadingImage={uploadingImage}
        onSave={handleUpdateBookmark} onImageUpload={handleImageUpload} onDeleteImage={handleDeleteImage}
        allTags={allTags}
      />

      {/* Export Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent className="modal-dark sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-[#FDF8EE]">Export Bookmarks</DialogTitle></DialogHeader>
          <div className="py-4 space-y-3">
            <button onClick={() => handleExport("json")} className="w-full btn-ghost text-left" data-testid="export-json-btn">Export as JSON</button>
            <button onClick={() => handleExport("markdown")} className="w-full btn-ghost text-left" data-testid="export-markdown-btn">Export as Markdown</button>
          </div>
        </DialogContent>
      </Dialog>

      <UpgradeModal open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen} currentPlan={currentPlan} onUpgrade={handleUpgrade} />

      {/* Cap-Hit Modal */}
      <Dialog open={capHitModalOpen} onOpenChange={setCapHitModalOpen}>
        <DialogContent className="modal-dark sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#FDF8EE] text-xl flex items-center gap-2">
              <span className="text-[#E8A030]">◈</span> You've reached your free limit
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-5">
            <p className="text-sm text-[#8C7A5A] leading-relaxed">
              Your 10 saved items are safe and still yours. Upgrade to Signal to keep saving.
            </p>

            <div className="space-y-2">
              {["Unlimited saves and volumes", "Ask Ryan — AI-powered search across your library", "Notion sync and image attachments", "Share volumes via link with collaborators"].map((b, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className="text-[#E8A030] text-xs shrink-0">&#10003;</span>
                  <span className="text-[12px] text-[#F5ECD0]">{b}</span>
                </div>
              ))}
            </div>

            <div className="bg-[rgba(232,160,48,0.04)] border border-[rgba(232,160,48,0.12)] p-4">
              <div className="flex items-center gap-6">
                <div className="flex-1 text-center p-3 border border-[rgba(232,160,48,0.1)]">
                  <div className="font-serif text-lg font-bold text-[#FDF8EE]">$15<span className="text-[10px] font-normal text-[#4A3A1A] font-mono">/mo</span></div>
                  <div className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mt-1">Monthly</div>
                </div>
                <div className="flex-1 text-center p-3 border-2 border-[#E8A030] bg-[rgba(232,160,48,0.06)] relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="bg-[#E8A030] text-[#0A0600] font-mono text-[7px] font-bold px-1.5 py-0.5 uppercase tracking-[0.5px]">Save 20%</span>
                  </div>
                  <div className="font-serif text-lg font-bold text-[#FDF8EE]">$144<span className="text-[10px] font-normal text-[#4A3A1A] font-mono">/yr</span></div>
                  <div className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mt-1">Annual</div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { trackEvent("upgrade_click", { source: "cap_modal" }); handleUpgrade("signal_monthly"); setCapHitModalOpen(false); }}
                className="btn-primary flex-1"
                data-testid="cap-hit-upgrade-btn"
              >
                Upgrade to Signal
              </button>
              <button
                onClick={() => setCapHitModalOpen(false)}
                className="btn-ghost flex-1"
                data-testid="cap-hit-later-btn"
              >
                Maybe later
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Processing Overlay */}
      {paymentProcessing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.15)] p-8 text-center">
            <div className="spinner-ember mx-auto mb-4"></div>
            <p className="font-mono text-[10px] text-[#8C7A5A] uppercase tracking-[1px]">Verifying payment...</p>
          </div>
        </div>
      )}

      <SmartGroupsModal open={smartGroupsOpen} onOpenChange={setSmartGroupsOpen} suggestions={smartSuggestions} onApply={applySmartGroup} />

      {graphOpen && <KnowledgeGraph onClose={() => setGraphOpen(false)} />}

      <AskRyanPanel isOpen={askRyanOpen} onClose={() => setAskRyanOpen(false)} subscription={subscription} onUpgrade={() => { setAskRyanOpen(false); setUpgradeModalOpen(true); }} />

      <SettingsModal
        open={settingsModalOpen} onOpenChange={setSettingsModalOpen}
        passedUser={passedUser} isPro={isPro} currentPlan={currentPlan} askRyanOpen={askRyanOpen} coveredByTeam={subscription.covered_by_team} subscriptionLoading={!!subscription.loading}
        notionStatus={notionStatus} notionToken={notionToken} setNotionToken={setNotionToken} notionSyncing={notionSyncing}
        onConnectNotion={handleConnectNotion} onNotionSync={handleNotionSync} onDisconnectNotion={handleDisconnectNotion}
      />

      <ShareModal
        open={shareModalOpen} onOpenChange={setShareModalOpen}
        shareLevel={shareLevel} setShareLevel={setShareLevel}
        shareInfo={shareInfo} sharingCollectionId={sharingCollectionId} currentPlan={currentPlan}
        collabEmail={collabEmail} setCollabEmail={setCollabEmail} collaborators={collaborators}
        onShareCollection={handleShareCollection} onUnshareCollection={handleUnshareCollection}
        onCopyShareLink={copyShareLink} onAddCollaborator={addCollaborator} onRemoveCollaborator={removeCollaborator}
      />

      <RelatedModal viewingBookmark={viewingBookmark} relatedBookmarks={relatedBookmarks} onClose={() => { setViewingBookmark(null); setRelatedBookmarks([]); }} />
    </div>
  );
};

export default Dashboard;
